import Box from '@mui/material/Box';
import { Editor } from '@tiptap/core';
import { useEditorState } from '@tiptap/react';
import { enqueueSnackbar } from 'notistack';
import { FC, useState } from 'react';
import {
  MdAutoFixHigh,
  MdDeleteOutline,
  MdImage,
  MdLink,
  MdOndemandVideo,
  MdTableChart,
  MdTableRows,
  MdViewColumn,
} from 'react-icons/md';

import { extractQuotaErrorMessage } from '../media-manager/process-handler';
import { HtmlIframe, HtmlImage, toVimeoEmbedSrc } from './extensions';
import { FORMAT_ITEMS, FormatFlagKey } from './format-items';
import { ToolbarBtn } from './toolbar-btn';
import { UPLOAD_PLACEHOLDER, uploadImage } from './upload-image';

type RichHtmlToolbarProps = {
  editor: Editor;
  isHtmlMode: boolean;
  showHtmlToggle: boolean;
  showImageUpload: boolean;
  showVideoEmbed: boolean;
  showTable: boolean;
  onToggleHtmlMode: () => void;
  onFormatHtml?: () => void;
  allowedFormats?: FormatFlagKey[];
  disabled?: boolean;
};

// A bare domain ("example.com") would otherwise become a relative link.
const withDefaultScheme = (url: string) => (/^([a-z][a-z0-9+.-]*:|[/#?])/i.test(url) ? url : `https://${url}`);

export const RichHtmlToolbar: FC<RichHtmlToolbarProps> = ({
  editor,
  isHtmlMode,
  showHtmlToggle,
  showImageUpload,
  showVideoEmbed,
  showTable,
  onToggleHtmlMode,
  onFormatHtml,
  allowedFormats,
  disabled = false,
}) => {
  const visibleFormats = allowedFormats
    ? FORMAT_ITEMS.filter(item => allowedFormats.includes(item.key))
    : FORMAT_ITEMS.filter(item => !item.optIn);
  const [isUploading, setIsUploading] = useState(false);

  const flags = useEditorState({
    editor,
    selector: ({ editor: ed }) => ({
      bold: ed.isActive('bold'),
      italic: ed.isActive('italic'),
      underline: ed.isActive('underline'),
      strike: ed.isActive('strike'),
      h1: ed.isActive('heading', { level: 1 }),
      h2: ed.isActive('heading', { level: 2 }),
      h3: ed.isActive('heading', { level: 3 }),
      bulletList: ed.isActive('bulletList'),
      orderedList: ed.isActive('orderedList'),
      blockquote: ed.isActive('blockquote'),
      codeBlock: ed.isActive('codeBlock'),
      subscript: ed.isActive('subscript'),
      superscript: ed.isActive('superscript'),
      link: ed.isActive('link'),
      // Drives the row/column controls, which only make sense with the caret
      // inside a table.
      inTable: ed.isActive('table'),
    }),
  });

  const editLink = () => {
    const previousUrl = editor.getAttributes('link').href as string | undefined;
    // eslint-disable-next-line no-alert
    const url = window.prompt('URL', previousUrl ?? '');
    if (url === null) {
      return;
    }
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: withDefaultScheme(url) })
      .run();
  };

  const insertVideo = () => {
    // eslint-disable-next-line no-alert
    const input = window.prompt('Vimeo video ID or link');
    if (input === null || input.trim() === '') {
      return;
    }
    const src = toVimeoEmbedSrc(input);
    if (!src) {
      enqueueSnackbar('That does not look like a Vimeo ID or link', { variant: 'error' });
      return;
    }
    editor
      .chain()
      .focus()
      .insertContent({
        type: HtmlIframe.name,
        attrs: { src, title: 'Vimeo video', allow: 'autoplay; fullscreen; picture-in-picture' },
      })
      .run();
  };

  const pickAndUploadImage = () => {
    if (isUploading) {
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) {
        return;
      }

      const insertAt = editor.state.selection.from;
      editor.chain().focus().insertContentAt(insertAt, UPLOAD_PLACEHOLDER).run();
      setIsUploading(true);

      try {
        const url = await uploadImage(file);
        editor
          .chain()
          .focus()
          .deleteRange({ from: insertAt, to: insertAt + UPLOAD_PLACEHOLDER.length })
          .insertContentAt(insertAt, { type: HtmlImage.name, attrs: { src: url, alt: file.name } })
          .run();
      } catch (error) {
        editor
          .chain()
          .focus()
          .deleteRange({ from: insertAt, to: insertAt + UPLOAD_PLACEHOLDER.length })
          .run();
        // Surface quota rejections (e.g. monthly 5MB personal upload limit)
        enqueueSnackbar(extractQuotaErrorMessage(error) ?? 'Image upload failed', { variant: 'error' });
      } finally {
        setIsUploading(false);
      }
    };
    input.click();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        flexWrap: 'wrap',
        p: 0.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.default',
      }}
    >
      {!isHtmlMode && (
        <>
          {visibleFormats.map(item => (
            <ToolbarBtn
              key={item.key}
              tooltip={item.tooltip}
              active={flags[item.key]}
              disabled={disabled}
              onClick={() => item.run(editor)}
            >
              <item.Icon size={18} />
            </ToolbarBtn>
          ))}
          <ToolbarBtn tooltip="Insert / edit link" active={flags.link} disabled={disabled} onClick={editLink}>
            <MdLink size={18} />
          </ToolbarBtn>
          {showImageUpload && (
            <ToolbarBtn
              tooltip={isUploading ? 'Uploading…' : 'Insert image'}
              active={isUploading}
              onClick={pickAndUploadImage}
            >
              <MdImage size={18} />
            </ToolbarBtn>
          )}
          {showVideoEmbed && (
            <ToolbarBtn tooltip="Insert Vimeo video" active={false} onClick={insertVideo}>
              <MdOndemandVideo size={18} />
            </ToolbarBtn>
          )}
          {showTable && (
            <>
              <ToolbarBtn
                tooltip="Insert table"
                active={flags.inTable}
                onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
              >
                <MdTableChart size={18} />
              </ToolbarBtn>
              {/* Row/column editing only appears with the caret inside a table —
                  otherwise the commands are no-ops and the buttons read as broken. */}
              {flags.inTable && (
                <>
                  <ToolbarBtn
                    tooltip="Add row below"
                    active={false}
                    onClick={() => editor.chain().focus().addRowAfter().run()}
                  >
                    <MdTableRows size={18} />
                  </ToolbarBtn>
                  <ToolbarBtn
                    tooltip="Add column after"
                    active={false}
                    onClick={() => editor.chain().focus().addColumnAfter().run()}
                  >
                    <MdViewColumn size={18} />
                  </ToolbarBtn>
                  <ToolbarBtn
                    tooltip="Delete row"
                    active={false}
                    onClick={() => editor.chain().focus().deleteRow().run()}
                  >
                    <MdTableRows size={18} style={{ opacity: 0.55 }} />
                  </ToolbarBtn>
                  <ToolbarBtn
                    tooltip="Delete column"
                    active={false}
                    onClick={() => editor.chain().focus().deleteColumn().run()}
                  >
                    <MdViewColumn size={18} style={{ opacity: 0.55 }} />
                  </ToolbarBtn>
                  <ToolbarBtn
                    tooltip="Delete table"
                    active={false}
                    onClick={() => editor.chain().focus().deleteTable().run()}
                  >
                    <MdDeleteOutline size={18} />
                  </ToolbarBtn>
                </>
              )}
            </>
          )}
        </>
      )}
      <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {isHtmlMode && onFormatHtml && (
          <ToolbarBtn tooltip="Format HTML" active={false} onClick={onFormatHtml}>
            <MdAutoFixHigh size={18} />
          </ToolbarBtn>
        )}
        {showHtmlToggle && (
          <ToolbarBtn
            tooltip={isHtmlMode ? 'Switch to visual editor' : 'Switch to HTML source'}
            active={isHtmlMode}
            onClick={onToggleHtmlMode}
          >
            {isHtmlMode ? 'Visual' : 'HTML'}
          </ToolbarBtn>
        )}
      </Box>
    </Box>
  );
};
